import * as React from 'react';
import * as _ from 'lodash';
import * as cookie from 'react-cookie';
import AppLayoutContextUpdater from 'login-components/Common/AppLayoutContextUpdater/AppLayoutContextUpdater';
import FormDialog from 'login-components/Common/Form/FormDialog/FormDialog';
import FormDialogFooter from 'login-components/Common/Form/FormDialog/FormDialogFooter';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import SecondaryButton from 'login-components/Common/Buttons/SecondaryButton';
import * as formUtils from 'login-utils/formUtils';
import * as oauthAPI from 'login-apis/oauthAPI';
import './OAuth.scss';

interface OAuthStates {
  confirmAuthorization: boolean;
  oAuthParams: OAuthParams | null;
  submitting: boolean;
}
interface OAuthScope {
  _id: string;
  description: string;
}
interface OAuthParams {
  clientName: string;
  requestedScopes: OAuthScope[];
}

class OAuth extends React.Component<ReactRouter.RouteComponentProps<{}, {}>, OAuthStates> {
  private form: HTMLFormElement;

  constructor(props) {
    super(props);
    this.state = {
      confirmAuthorization: false,
      oAuthParams: null,
      submitting: false,
    };
  }

  setAuthorizationStateAndSubmit(e: React.MouseEvent<HTMLButtonElement>, confirmAuthorization: boolean) {
    // Prevent default so the native form won't submit after clicking the button
    e.preventDefault();
    // Be extra cautious about resubmitting form. Whenever this is submitting, just return;
    if (this.state.submitting) {
      return;
    }
    this.setState(
      {
        confirmAuthorization,
        submitting: true,
      },
      () => {
        formUtils.submitForm(this.form as HTMLFormElement);
      },
    );
  }

  async componentDidMount() {
    // Uncomment this piece of code if you would like to test OAuth UI. Then open http://localhost:8080/#/authorize
    /*
    return this.setState({
      oAuthParams: {
        clientName: 'Client App',
        requestedScopes: [
          {
            _id: 'config:read',
            description: 'View configuration information.',
          },
          {
            _id: 'documents:write',
            description: 'Create, update, and modify documents.',
          },
          {
            _id: 'loans:read',
            description: 'View loan information.',
          },
          {
            _id: 'loans:write',
            description: 'Create, update, and modify loans.',
          },
          {
            _id: 'loans:read2',
            description: 'View loan information.',
          },
          {
            _id: 'loans:write2',
            description: 'Create, update, and modify loans.',
          },
        ],
      },
    });
    */

    try {
      const res = await oauthAPI.oAuthTransaction();
      if (res.data) {
        this.setState({ oAuthParams: res.data as OAuthParams });
      }
    } catch (e) {
      oauthAPI.formLogout({ message: 'oauthError' });
    }
  }

  render() {
    const { oAuthParams, confirmAuthorization, submitting } = this.state;
    const csrfToken = cookie.load('XSRF-TOKEN');
    const appTitle = 'Authorize Application';
    return (
      <div className="Oauth" id="it-OAuth">
        <AppLayoutContextUpdater appTitle={appTitle} footerConfig={null} appDocumentTitle={appTitle}>
          <div className="Oauth-dialog" id="it-Oauth-dialog">
            <form
              action="/auth/oauth/authorize/decision"
              method="post"
              ref={(form: HTMLFormElement) => (this.form = form)}
            >
              <fieldset>
                <input type="hidden" name="_csrf" value={csrfToken} />
                <input type="hidden" name="confirmAuthorization" value={String(confirmAuthorization)} />
                {oAuthParams && (
                  <FormDialog>
                    <div className="Oauth-dialogBody">
                      <div className="Oauth-bodyContent Oauth-subtitle" id="it-Oauth-clientName">
                        Do you authorize <strong>{oAuthParams.clientName}</strong> to connect with your Blend web
                        account?
                      </div>
                      <div className="Oauth-bodyContent">
                        This application <strong>will be able to:</strong>
                      </div>
                      <ol className="Oauth-bodyContent Oauth-scopeItems">
                        {_.map(oAuthParams.requestedScopes, (scope: OAuthScope) => {
                          return (
                            <li className="Oauth-scopeItem" id={`it-Oauth-scope-${scope._id}`} key={scope._id}>
                              {scope.description}
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                    <FormDialogFooter className="Oauth-formWrapper">
                      <div className="Oauth-buttonWrapper">
                        <PrimaryButton
                          buttonId="it-Oauth-confirmButton"
                          text="Yes, authorize"
                          type="button"
                          disabled={submitting}
                          onClick={e => this.setAuthorizationStateAndSubmit(e, true)}
                        />
                      </div>
                      <div className="Oauth-buttonWrapper">
                        <SecondaryButton
                          buttonId="it-Oauth-cancelButton"
                          text="No, cancel"
                          type="button"
                          disabled={submitting}
                          onClick={e => this.setAuthorizationStateAndSubmit(e, false)}
                        />
                      </div>
                      <div className="Oauth-footerContent">
                        By clicking 'Yes, authorize', you agree to share information with {oAuthParams.clientName} and
                        to allow the information to be used in accordance with the terms of use and privacy policy of{' '}
                        {oAuthParams.clientName}. Blend disclaims any liability relating to your use of{' '}
                        {oAuthParams.clientName}.
                      </div>
                    </FormDialogFooter>
                  </FormDialog>
                )}
              </fieldset>
            </form>
          </div>
        </AppLayoutContextUpdater>
      </div>
    );
  }
}

export default OAuth;
